import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, RotateCcw, Upload } from "lucide-react";
import { siteLogoUrl } from "@/components/shared/SiteLogo";

const B = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// Settings > Pages > Logo: replace the logo used across the site, portals and emails
export const SiteLogoCard = () => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(0);
  const input = useRef(null);
  useEffect(() => { axios.get(`${B}/api/site-logo/info`, auth()).then((r) => setInfo(r.data)).catch(() => setInfo({ custom: false })); }, []);
  const bump = (data) => { localStorage.setItem("site_logo_v", String(Date.now())); setInfo(data); setV(Date.now()); };
  const run = async (fn, ok) => {
    setBusy(true);
    try { bump((await fn()).data); toast.success(ok); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update the logo"); } finally { setBusy(false); }
  };
  const upload = (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    run(() => axios.post(`${B}/api/site-logo`, fd, auth()), "Logo updated. It shows everywhere within a few minutes.");
  };
  return (
    <Card className="border-slate-200 mb-6" data-testid="site-logo-card">
      <CardContent className="p-5 flex items-center gap-5 flex-wrap">
        <div className="w-48 h-24 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center p-2">
          <img key={v} src={siteLogoUrl()} alt="Current logo" className="max-h-full max-w-full object-contain" data-testid="site-logo-preview"
            onError={(e) => { if (!e.currentTarget.src.endsWith("/pmw-logo.png")) e.currentTarget.src = "/pmw-logo.png"; }} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><ImageIcon className="w-4 h-4 text-teal" />Logo</h3>
          <p className="text-sm text-slate-500" data-testid="site-logo-status">
            {info?.custom ? `Custom logo (${info.file_name || "uploaded"}).` : "Using the built-in Prime Media Wireless logo."} Used on the website, login, admin and dealer portals and emails. PNG, JPG or WebP, under 2 MB.
          </p>
        </div>
        <div className="flex gap-2">
          <input ref={input} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="site-logo-input" />
          <Button size="sm" onClick={() => input.current?.click()} disabled={busy} data-testid="site-logo-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Upload new logo
          </Button>
          {info?.custom && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => axios.delete(`${B}/api/site-logo`, auth()), "Back to the built-in logo")} data-testid="site-logo-reset-btn">
              <RotateCcw className="w-4 h-4 mr-1" />Use built-in logo
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
